import { matchPath } from "react-router-dom";

/**
 * Accountant allowlist (external bookkeeper, owner ruling Artem 2026-10-06).
 * An `accountant` staff_roles session sees ONLY: the dashboard, the staff
 * list + staff profile main view, every billing / payables route, and the
 * financial cockpit (/cockpit/financials, owner ruling 2026-10-07).
 * Exclusive gate, same precedence model as restrictedAllowlist.ts, applied
 * for EVERY gate type (incl. 'authenticated'/'privileged'):
 * RouteGateGuard redirects any other registry path to ACCOUNTANT_HOME_PATH,
 * AccountantRouteEnforcer (around the authenticated Layout) does the same by
 * live pathname for anything not wrapped in RouteGateGuard, and Layout hides
 * every other rail entry. RLS is the real data boundary.
 * RouteGateGuard matches registry path patterns verbatim; the enforcer
 * matches the live pathname against the same patterns via matchPath.
 */
export const ACCOUNTANT_ALLOWED_PATHS: ReadonlySet<string> = new Set([
  // Dashboard
  "/",
  "/overzicht",
  // Staff list + profile (StaffProfile limits the accountant to Overview)
  "/staff",
  "/staff/:id",
  // Billing (all hub tabs + parent-invoice finance pages) and payables.
  // Most /billing/* and every /payables/* entry is a registry redirect into
  // /billing?tab=...; they never reach RouteGateGuard, but the pathname
  // enforcer sees them before the redirect fires, so they stay listed to
  // keep legacy links working for the accountant.
  "/billing",
  "/billing/payments",
  "/billing/open-invoices",
  "/billing/runs",
  "/billing/tarieven",
  "/billing/families/:debtorNumber",
  "/billing/invoices",
  "/billing/invoices/:invoiceNumber",
  // Books bridge (ledger vs LMS tie-out), moneyFlows-gated page.
  "/billing/books",
  "/payables",
  "/payables/submit",
  "/payables/personal",
  "/payables/review",
  "/payables/bank",
  "/payables/flags",
  "/payables/match",
  "/payables/inbox",
  // Financial cockpit (debtors, open invoices, payment tiers, leakage).
  "/cockpit/financials",
]);

/**
 * Must be a real, allow-listed page — never "/": the index RootRoute only
 * redirects (it branches accountants here), so pointing home at "/" loops.
 */
export const ACCOUNTANT_HOME_PATH = "/overzicht";

export function isAccountantAllowedRoutePath(routePath: string): boolean {
  return ACCOUNTANT_ALLOWED_PATHS.has(routePath);
}

/** Live-URL variant for AccountantRouteEnforcer (routes outside RouteGateGuard). */
export function isAccountantAllowedPathname(pathname: string): boolean {
  return [...ACCOUNTANT_ALLOWED_PATHS].some(
    (pattern) => matchPath({ path: pattern, end: true }, pathname) !== null,
  );
}
